import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { Event } from '../models';
import AlertBox from '../components/AlertBox';
import { API_BASE_URL } from '../config';
import { formatDateTimeEST } from '../utils/dateUtils';

const SITE_URL = 'https://www.soundx724.com';

const EventDetail: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);


  const fetchEvent = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/events/${uuid}`);
      if (!response.ok) throw new Error('Failed to fetch event');
      const data = await response.json();
      setEvent(data.event);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleDelete = async () => {
    if (!event) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/events/${event.uuid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete event');

      navigate('/events');
    } catch (err: any) {
      setError(err.message);
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateTimeEST(dateString);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-xl text-secondary-600">Loading event...</div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container mx-auto px-4 py-12">
        <AlertBox type="error" message={error || 'Event not found'} />
        <Link to="/events" className="inline-block mt-4 text-primary-600 hover:text-primary-700">
          ← Back to Events
        </Link>
      </div>
    );
  }

  const resolveOgImage = (imageFiles?: string) => {
    if (!imageFiles) return `${SITE_URL}/img/bg-img/bg-0.png`;
    if (/^https?:\/\//i.test(imageFiles)) return imageFiles;
    const path = imageFiles.startsWith('/') ? imageFiles : `/img/events-img/${imageFiles}`;
    return `${SITE_URL}${path}`;
  };

  const metaDescription = event.description
    ? event.description.slice(0, 160)
    : `${event.title} — an event at ${event.event_address}`;

  const canonicalUrl = `${SITE_URL}/events/${event.uuid}`;
  const ogImage = resolveOgImage(event.image_files);

  return (
    <>
    <Helmet>
      <title>{event.title} | SoundX724</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={`${event.title} | SoundX724`} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="SoundX724" />
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${event.title} | SoundX724`} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-yellow-50 py-12">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <Link 
          to="/events" 
          className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6 font-medium"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Events
        </Link>

        {/* Main Content */}
        <article className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Hero Image */}

          {/* Content Section */}
          <div className="p-4 sm:p-8 md:p-12">
            {/* Event Type Badge */}
            <div className="mb-4">
              <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
                event.event_type === 'concert' 
                  ? 'bg-primary-100 text-primary-700' 
                  : 'bg-accent-100 text-accent-700'
              }`}>
                {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-elegant font-bold text-secondary-900 mb-6">
              {event.title}
            </h1>

            {/* Meta Information */}
            <div className="grid md:grid-cols-2 gap-6 mb-8 p-6 bg-gradient-to-br from-primary-50 to-warm-50 rounded-lg border border-primary-200">
              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-primary-600 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm text-secondary-600 font-semibold">Start Date</p>
                  <p className="text-base text-secondary-900">{formatDate(event.event_start_date)}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-primary-600 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm text-secondary-600 font-semibold">End Date</p>
                  <p className="text-base text-secondary-900">{formatDate(event.event_end_date)}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-primary-600 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div>
                  <p className="text-sm text-secondary-600 font-semibold">Location</p>
                  <p className="text-base text-secondary-900">{event.event_address}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-primary-600 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm text-secondary-600 font-semibold">Pricing</p>
                  <p className="text-base text-secondary-900">
                    <span className="block">Adults: ${event.event_ticket_price_adults}</span>
                    <span className="block">Kids: ${event.event_ticket_price_kids}</span>
                    {event.discount_allowed && <span className="block text-green-600 font-semibold">✓ Discounts Available</span>}
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-secondary-900 mb-4">About This Event</h2>
                {event.image_files && (
                    <div className="w-full flex justify-center items-center bg-secondary-100 py-8">
                      <img 
                        src={event.image_files.startsWith('/') ? event.image_files : `/img/events-img/${event.image_files}`}
                        alt={event.title || 'Event image'}
                        className="w-4/5 sm:w-2/5 h-auto object-cover rounded-2xl"
                      />
                    </div>
                )}
              <p className="text-lg text-secondary-700 leading-relaxed">
                {event.description}
              </p>
            </div>

            {/* Full Content */}
            {event.content && (
              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-secondary-900 mb-4">Event Details</h2>
                <div 
                  className="text-secondary-700 leading-relaxed overflow-x-auto break-words
                    [&_p]:mb-4 [&_p]:leading-relaxed
                    [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:mt-4
                    [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mb-3 [&_h2]:mt-4
                    [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-2 [&_h3]:mt-3
                    [&_strong]:font-semibold
                    [&_em]:italic
                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
                    [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
                    [&_li]:mb-1
                    [&_a]:text-primary-600 [&_a]:underline [&_a:hover]:text-primary-800
                    [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-4
                    [&_table]:w-full [&_table]:border-collapse [&_table]:my-4
                    [&_td]:border [&_td]:border-secondary-200 [&_td]:p-2
                    [&_th]:border [&_th]:border-secondary-200 [&_th]:p-2 [&_th]:font-semibold [&_th]:bg-secondary-50
                    [&_blockquote]:border-l-4 [&_blockquote]:border-primary-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4"
                  dangerouslySetInnerHTML={{ __html: event.content }}
                />
              </div>
            )}

            {/* Video URLs */}
            {event.video_urls && (
              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-secondary-900 mb-4">Videos</h2>
                <div className="space-y-2">
                  {(() => {
                    let urls: string[] = [];
                    
                    if (Array.isArray(event.video_urls)) {
                      urls = event.video_urls;
                    } else if (typeof event.video_urls === 'string') {
                      try {
                        // Try to parse as JSON array
                        const parsed = JSON.parse(event.video_urls);
                        urls = Array.isArray(parsed) ? parsed : [event.video_urls];
                      } catch {
                        // If not JSON, treat as single URL
                        urls = [event.video_urls];
                      }
                    }
                    
                    return urls.map((url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        Link {index + 1}
                      </a>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Book Tickets Button for Public */}
            {new Date(event.event_start_date) > new Date() && (event.is_disabled === false) && (
              <div className="mb-8">
                <Link
                  to={`/book-tickets/${event.uuid}`}
                  className="inline-block w-full md:w-auto px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white text-center rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  🎫 Book Tickets Now
                </Link>
              </div>
            )}

            {/* Admin Actions */}
            {user && user.role === 'admin' && (
              <div className="mt-12 pt-8 border-t border-secondary-200">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Admin Actions</h3>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => navigate(`/events/edit/${event.uuid}`)}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit Event</span>
                  </button>
                  
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Delete Event</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </article>

        {/* Registration CTA */}
        {/* <div className="mt-8 bg-gradient-to-r from-primary-600 via-primary-700 to-warm-700 rounded-xl shadow-lg p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Attend?</h2>
          <p className="text-lg mb-6">Register now to secure your spot at this amazing event!</p>
          <button className="px-8 py-4 bg-white text-primary-700 rounded-lg hover:bg-warm-50 transition-colors font-bold text-lg shadow-md">
            Register Now
          </button>
        </div> */}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-secondary-900 mb-4">Confirm Delete</h3>
            <p className="text-secondary-700 mb-6">
              Are you sure you want to delete "{event.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-6 py-3 border border-secondary-300 text-secondary-700 rounded-lg hover:bg-secondary-50 transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'Deleting...' : 'Delete Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default EventDetail;
